import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Send, Loader2, ArrowLeft, Save, Paperclip, StickyNote, Eye, FileSearch } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge, PriorityBadge } from "@/components/help/SupportBadges";
import AdminRequestsTab from "@/components/help/AdminRequestsTab";
import AdminCannedTab from "@/components/help/AdminCannedTab";
import AdminSlaTab from "@/components/help/AdminSlaTab";
import { AttachmentChip } from "@/pages/SupportTicketDetail";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const inputCls = "w-full rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60";
const sel = "rounded-md border border-line bg-bg px-2.5 py-1.5 text-xs text-ink-primary focus:outline-none";

const Stat = ({ label, value, tone = "text-ink-primary", testid }) => (
  <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid={testid}>
    <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mb-1">{label}</div>
    <div className={`text-xl font-bold ${tone}`}>{value ?? "—"}</div>
  </div>
);

const BreakdownCard = ({ title, entries }) => (
  <div className="rounded-xl border border-line bg-bg-surface p-4">
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3">{title}</div>
    {Object.keys(entries || {}).length === 0
      ? <div className="text-xs text-ink-muted">No data yet</div>
      : Object.entries(entries).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k, v]) => (
        <div key={k} className="flex items-center justify-between py-1 text-xs">
          <span className="text-ink-secondary truncate">{k}</span>
          <span className="text-ink-primary font-semibold ml-2">{v}</span>
        </div>
      ))}
  </div>
);

function TicketWorkspace() {
  const { user: me } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [meta, setMeta] = useState(null);
  const [agents, setAgents] = useState([]);
  const [canned, setCanned] = useState([]);
  const [f, setF] = useState({ status: "", priority: "", category: "", q: "" });
  const [active, setActive] = useState(null); // {ticket, messages}
  const [reply, setReply] = useState("");
  const [internalMode, setInternalMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = (filters = f) => {
    const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/admin/helpdesk/tickets${qs ? `?${qs}` : ""}`).then((r) => setTickets(r.data.tickets)).catch(() => {});
  };
  useEffect(() => {
    api.get("/helpdesk/meta").then((r) => setMeta(r.data));
    api.get("/admin/helpdesk/agents").then((r) => setAgents(r.data.agents)).catch(() => {});
    api.get("/admin/helpdesk/canned").then((r) => setCanned(r.data.canned)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = (ref) =>
    api.get(`/admin/helpdesk/tickets/${ref}`).then((r) => setActive(r.data)).catch(() => toast.error("Couldn't open ticket"));

  const patch = async (updates) => {
    setBusy(true);
    try {
      await api.patch(`/admin/helpdesk/tickets/${active.ticket.reference}`, updates);
      await open(active.ticket.reference);
      load();
      toast.success("Updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  const sendReply = async () => {
    if (reply.trim().length < 2) return;
    setBusy(true);
    try {
      if (internalMode) {
        await api.post(`/admin/helpdesk/tickets/${active.ticket.reference}/internal-note`, { body: reply.trim() });
        toast.success("Internal note added — customer can't see it");
      } else {
        await api.post(`/admin/helpdesk/tickets/${active.ticket.reference}/reply`, { body: reply.trim() });
      }
      setReply("");
      await open(active.ticket.reference);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reply failed"); }
    finally { setBusy(false); }
  };
  const insertCanned = (cannedId) => {
    const c = canned.find((x) => x.canned_id === cannedId);
    if (!c) return;
    const first = (active?.ticket?.name || active?.ticket?.email || "there").split(" ")[0];
    setReply(c.body.replaceAll("{first_name}", first));
    setInternalMode(false);
  };
  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/helpdesk/tickets/${active.ticket.reference}/attachments`, fd);
      toast.success("File attached");
      await open(active.ticket.reference);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const impersonate = async () => {
    try {
      const { data } = await api.post("/admin/impersonate", {
        user_id: active.ticket.user_id,
        reason: `Support ticket ${active.ticket.reference}`,
      });
      toast.success(`Now viewing as ${data.target.name || data.target.email}`);
      window.location.href = "/dashboard";
    } catch (e) { toast.error(e?.response?.data?.detail || "Impersonation failed"); }
  };

  if (active) {
    const t = active.ticket;
    return (
      <div className="space-y-4" data-testid="admin-ticket-detail">
        <button type="button" onClick={() => setActive(null)} data-testid="admin-ticket-back"
          className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary">
          <ArrowLeft className="h-3.5 w-3.5" /> Ticket queue
        </button>
        <div className="rounded-xl border border-line bg-bg-surface p-4">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className="font-mono text-[11px] text-brand-amber">{t.reference}</span>
            <StatusBadge status={t.status} /><PriorityBadge priority={t.priority} />
          </div>
          <div className="text-sm font-semibold text-ink-primary">{t.subject}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 font-mono text-[9px] uppercase tracking-wider text-ink-muted">
            <span>{t.name || t.email}</span><span>{t.category_label}</span>
            {t.station_name && <span>{t.station_name}</span>}
            {t.affected_feature && <span>{t.affected_feature}</span>}
            {t.integration_provider && <span>{t.integration_provider}</span>}
            <span>{t.browser} · {t.device_type} · {t.os}</span>
            <span className={t.allow_logs ? "text-emerald-300" : ""}>Logs {t.allow_logs ? "allowed" : "not allowed"}</span>
            <span className={t.allow_impersonation ? "text-emerald-300" : ""}>Impersonation {t.allow_impersonation ? "allowed" : "not allowed"}</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <select className={sel} value={t.status} data-testid="admin-ticket-status-select"
              onChange={(e) => patch({ status: e.target.value })} disabled={busy}>
              {(meta?.statuses || []).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
            <select className={sel} value={t.priority} data-testid="admin-ticket-priority-select"
              onChange={(e) => patch({ priority: e.target.value })} disabled={busy}>
              {(meta?.priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select className={sel} value={t.assignee_id || ""} data-testid="admin-ticket-assignee-select"
              onChange={(e) => patch({ assignee_id: e.target.value })} disabled={busy}>
              <option value="">Unassigned</option>
              {agents.map((a) => <option key={a.user_id} value={a.user_id}>{a.label} ({a.kind.replace(/_/g, " ")})</option>)}
            </select>
            <select className={sel} value={t.department || ""} data-testid="admin-ticket-department-select"
              onChange={(e) => patch({ department: e.target.value })} disabled={busy}>
              <option value="">No escalation</option>
              <option value="technical">Escalate: technical</option>
              <option value="billing">Escalate: billing</option>
              <option value="manager">Escalate: manager</option>
            </select>
            {me?.role === "super_admin" && t.user_id && (
              <button type="button" onClick={impersonate} disabled={!t.allow_impersonation} data-testid="admin-impersonate-btn"
                title={t.allow_impersonation ? "Open the app as this customer (30 min, audit-logged)" : "Customer didn't grant impersonation consent on this ticket"}
                className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/50 bg-rose-500/10 px-3 py-1.5 text-xs font-semibold text-rose-300 hover:bg-rose-500/20 transition disabled:opacity-40 disabled:cursor-not-allowed">
                <Eye className="h-3.5 w-3.5" /> View as customer
              </button>
            )}
          </div>
        </div>
        {t.diagnostics && (
          <details className="rounded-xl border border-line bg-bg-surface p-4" data-testid="admin-ticket-diagnostics">
            <summary className="cursor-pointer inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-cyan-300">
              <FileSearch className="h-3.5 w-3.5" /> Diagnostic snapshot (captured {new Date(t.diagnostics.captured_at).toLocaleString()})
            </summary>
            <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
              <div><span className="text-ink-muted">Plan:</span> <span className="text-ink-primary">{t.diagnostics.user?.plan}</span> <span className="text-ink-muted">({t.diagnostics.user?.subscription_status || "—"})</span></div>
              <div><span className="text-ink-muted">Station:</span> <span className="text-ink-primary">{t.diagnostics.station?.name || "—"}</span></div>
              <div><span className="text-ink-muted">Credits:</span> <span className="text-ink-primary">
                {t.diagnostics.credits ? `${t.diagnostics.credits.monthly_used ?? 0}/${t.diagnostics.credits.monthly_allowance ?? 0} used · top-up ${t.diagnostics.credits.topup_balance ?? 0}` : "—"}</span></div>
              <div><span className="text-ink-muted">Integrations:</span> <span className="text-ink-primary">
                {(t.diagnostics.integrations || []).length ? t.diagnostics.integrations.map((i) => i.provider || i.kind).join(", ") : "none"}</span></div>
            </div>
            {(t.diagnostics.recent_errors || []).length > 0 && (
              <div className="mt-3">
                <div className="font-mono text-[9px] uppercase tracking-wider text-rose-300 mb-1">Recent errors</div>
                {t.diagnostics.recent_errors.map((e, i) => (
                  <div key={i} className="text-[11px] text-ink-secondary py-0.5 border-b border-line last:border-0">
                    <span className="font-mono text-[9px] text-ink-muted mr-2">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    {e.title} — {e.detail}
                  </div>
                ))}
              </div>
            )}
          </details>
        )}
        <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1" data-testid="admin-ticket-messages">
          {active.messages.map((m) => (
            <div key={m.message_id} className={`rounded-lg border p-3 ${
              m.sender_type === "internal" ? "border-amber-500/50 bg-amber-500/10"
              : m.sender_type === "agent" ? "border-brand-amber/30 bg-brand-amber/5"
              : m.sender_type === "system" ? "border-dashed border-line"
              : "border-line bg-bg-elevated/50"}`}
              data-testid={`admin-msg-${m.sender_type}`}>
              {m.sender_type === "internal" && (
                <div className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-amber-300 mb-1.5"
                  data-testid="internal-note-marker">
                  <StickyNote className="h-3 w-3" /> Internal Note — Not Visible to Customer
                </div>
              )}
              <div className="flex items-center gap-2 text-[10px] font-mono text-ink-muted mb-1">
                <span className="uppercase tracking-wider">{m.sender_type === "customer" ? "Customer" : m.sender_name}</span>
                <span className="ml-auto">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <div className="text-xs text-ink-secondary whitespace-pre-wrap">{m.body}</div>
              {m.attachment && <AttachmentChip att={m.attachment} />}
            </div>
          ))}
        </div>
        <div className={`rounded-xl border p-3 ${internalMode ? "border-amber-500/50 bg-amber-500/5" : "border-line bg-bg-surface"}`}>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px] text-ink-secondary" data-testid="internal-note-toggle">
              <input type="checkbox" checked={internalMode} onChange={(e) => setInternalMode(e.target.checked)} className="accent-[#f59e0b]" />
              <StickyNote className="h-3 w-3 text-amber-300" /> Internal note (hidden from customer)
            </label>
            {!internalMode && canned.length > 0 && (
              <select className={sel} value="" data-testid="canned-picker"
                onChange={(e) => insertCanned(e.target.value)}>
                <option value="">Insert canned response…</option>
                {canned.map((c) => <option key={c.canned_id} value={c.canned_id}>{c.title}</option>)}
              </select>
            )}
          </div>
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} data-testid="admin-reply-input"
            placeholder={internalMode ? "Write an internal note — the customer will never see this…" : "Reply to the customer…"}
            className={`${inputCls} min-h-[80px]`} />
          <div className="flex items-center justify-between mt-2">
            <label className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary transition cursor-pointer">
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />} Attach
              <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.webp,.pdf,.txt,.csv,.log"
                onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="admin-attach-input" />
            </label>
            <button type="button" onClick={sendReply} disabled={busy || reply.trim().length < 2} data-testid="admin-reply-btn"
              className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-semibold disabled:opacity-50 ${
                internalMode ? "bg-amber-400 text-black" : "bg-brand-amber text-black"}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {internalMode ? "Add internal note" : "Send reply"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-ticket-queue">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-muted" />
          <input className={`${inputCls} !pl-9`} value={f.q} placeholder="Search reference, subject, email…"
            data-testid="admin-ticket-search"
            onChange={(e) => { const n = { ...f, q: e.target.value }; setF(n); }}
            onKeyDown={(e) => e.key === "Enter" && load(f)} />
        </div>
        <select className={sel} value={f.status} data-testid="admin-filter-status"
          onChange={(e) => { const n = { ...f, status: e.target.value }; setF(n); load(n); }}>
          <option value="">All statuses</option>
          {(meta?.statuses || []).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select className={sel} value={f.priority}
          onChange={(e) => { const n = { ...f, priority: e.target.value }; setF(n); load(n); }}>
          <option value="">All priorities</option>
          {(meta?.priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        {tickets.map((t) => (
          <button key={t.reference} type="button" onClick={() => open(t.reference)}
            data-testid={`admin-ticket-row-${t.reference}`}
            className="w-full text-left rounded-lg border border-line bg-bg-surface px-4 py-3 hover:border-brand-amber/40 transition">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] text-brand-amber">{t.reference}</span>
              <StatusBadge status={t.status} /><PriorityBadge priority={t.priority} />
              <span className="ml-auto font-mono text-[9px] text-ink-muted">{new Date(t.updated_at).toLocaleString()}</span>
            </div>
            <div className="text-sm text-ink-primary mt-1 truncate">{t.subject}</div>
            <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mt-0.5">
              {t.name || t.email} · {t.category_label}{t.station_name ? ` · ${t.station_name}` : ""}
            </div>
          </button>
        ))}
        {tickets.length === 0 && <div className="text-sm text-ink-muted py-6 text-center">No tickets match.</div>}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState({ support_email: "", support_hours: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/helpdesk/settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/helpdesk/settings", s); toast.success("Support settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-lg space-y-4" data-testid="admin-support-settings">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">Support email (shown to customers)</div>
        <input className={inputCls} value={s.support_email} data-testid="settings-support-email"
          onChange={(e) => setS({ ...s, support_email: e.target.value })} placeholder="support@yourdomain.com" />
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">Support hours (shown to customers)</div>
        <input className={inputCls} value={s.support_hours} data-testid="settings-support-hours"
          onChange={(e) => setS({ ...s, support_hours: e.target.value })} placeholder="Mon–Fri, 9:00–17:00 ET" />
      </div>
      <p className="text-[11px] text-ink-muted">Leave blank to hide — nothing fake is ever shown to customers.</p>
      <button type="button" onClick={save} disabled={busy} data-testid="settings-save-btn"
        className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save settings
      </button>
    </div>
  );
}

function StaffTab() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("support_agent");
  const load = () => api.get("/admin/helpdesk/staff").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const grant = async () => {
    if (!email.trim()) return;
    try {
      const { data: res } = await api.get(`/admin/onboarding/users?q=${encodeURIComponent(email.trim())}`);
      const u = (res.users || [])[0];
      if (!u) { toast.error("No user found with that email"); return; }
      await api.patch(`/admin/helpdesk/staff/${u.user_id}`, { support_role: role });
      toast.success(`${u.email} is now ${role.replace(/_/g, " ")}`);
      setEmail(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't assign role"); }
  };
  const revoke = async (uid) => {
    try { await api.patch(`/admin/helpdesk/staff/${uid}`, { support_role: null }); load(); toast.success("Role removed"); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="max-w-2xl space-y-4" data-testid="admin-support-staff">
      <div className="flex flex-wrap gap-2">
        <input className={`${inputCls} flex-1 min-w-[200px]`} value={email} placeholder="User email…"
          data-testid="staff-email-input" onChange={(e) => setEmail(e.target.value)} />
        <select className={sel} value={role} onChange={(e) => setRole(e.target.value)} data-testid="staff-role-select">
          {(data?.roles || []).map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
        </select>
        <button type="button" onClick={grant} data-testid="staff-grant-btn"
          className="rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black">Assign role</button>
      </div>
      <div className="space-y-1.5">
        {(data?.staff || []).map((s) => (
          <div key={s.user_id} className="flex items-center justify-between rounded-lg border border-line bg-bg-surface px-4 py-2.5"
            data-testid={`staff-row-${s.user_id}`}>
            <div>
              <div className="text-sm text-ink-primary">{s.name || s.email}</div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-brand-amber">{(s.support_role || "").replace(/_/g, " ")}</div>
            </div>
            <button type="button" onClick={() => revoke(s.user_id)}
              className="text-[11px] text-ink-muted hover:text-rose-300">Remove</button>
          </div>
        ))}
        {data && data.staff.length === 0 && <div className="text-sm text-ink-muted">No support staff yet — assign a role above.</div>}
      </div>
      <p className="text-[11px] text-ink-muted">Support roles grant only support permissions — never full admin access. Super admins always have full support access.</p>
    </div>
  );
}

export default function AdminSupport() {
  const [ov, setOv] = useState(null);
  useEffect(() => { api.get("/admin/helpdesk/overview").then((r) => setOv(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-6" data-testid="admin-support-page">
      <PageHeader eyebrow="// ADMIN · SUPPORT" title="Support Dashboard"
        description="Live ticket queue, response metrics, and support configuration." />
      {ov && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <Stat label="New" value={ov.counts.new} tone="text-cyan-300" testid="sup-stat-new" />
            <Stat label="Open" value={ov.counts.open} tone="text-blue-300" testid="sup-stat-open" />
            <Stat label="Urgent" value={ov.urgent_open} tone="text-rose-300" testid="sup-stat-urgent" />
            <Stat label="Escalated" value={ov.counts.escalated} tone="text-rose-300" />
            <Stat label="Waiting customer" value={ov.counts.waiting_customer} tone="text-amber-300" />
            <Stat label="Resolved" value={ov.counts.resolved} tone="text-emerald-300" />
            <Stat label="Closed" value={ov.counts.closed} />
          </div>
          <div className="grid grid-cols-2 gap-2 max-w-md">
            <Stat label="Avg first response" testid="sup-stat-first-response"
              value={ov.avg_first_response_hours != null ? `${ov.avg_first_response_hours}h` : "No data yet"} />
            <Stat label="Avg resolution" testid="sup-stat-resolution"
              value={ov.avg_resolution_hours != null ? `${ov.avg_resolution_hours}h` : "No data yet"} />
          </div>
        </>
      )}
      <Tabs defaultValue="tickets">
        <TabsList className="bg-bg-surface border border-line">
          <TabsTrigger value="tickets" data-testid="tab-tickets">Tickets</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Requests</TabsTrigger>
          <TabsTrigger value="canned" data-testid="tab-canned">Canned</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">Insights</TabsTrigger>
          <TabsTrigger value="sla" data-testid="tab-sla">SLA</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff">Staff</TabsTrigger>
        </TabsList>
        <TabsContent value="tickets" className="mt-4"><TicketWorkspace /></TabsContent>
        <TabsContent value="requests" className="mt-4"><AdminRequestsTab /></TabsContent>
        <TabsContent value="canned" className="mt-4"><AdminCannedTab /></TabsContent>
        <TabsContent value="insights" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-support-insights">
            <BreakdownCard title="Tickets by category" entries={ov?.by_category} />
            <BreakdownCard title="Tickets by feature" entries={ov?.by_feature} />
            <BreakdownCard title="Tickets by integration" entries={ov?.by_provider} />
            <div className="rounded-xl border border-line bg-bg-surface p-4 sm:col-span-2 lg:col-span-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3">Help articles with poor ratings</div>
              {(ov?.poor_rated_articles || []).length === 0
                ? <div className="text-xs text-ink-muted">No poorly-rated articles — nice.</div>
                : ov.poor_rated_articles.map((a) => (
                  <div key={a.slug} className="flex items-center justify-between py-1.5 text-xs border-b border-line last:border-0">
                    <span className="text-ink-secondary truncate">{a.title}</span>
                    <span className="font-mono text-[10px] ml-2"><span className="text-emerald-300">{a.helpful_yes}↑</span> <span className="text-rose-300">{a.helpful_no}↓</span></span>
                  </div>
                ))}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="sla" className="mt-4"><AdminSlaTab /></TabsContent>
        <TabsContent value="settings" className="mt-4"><SettingsTab /></TabsContent>
        <TabsContent value="staff" className="mt-4"><StaffTab /></TabsContent>
      </Tabs>
    </div>
  );
}
